import React, { useContext } from 'react';
import Cookies from 'universal-cookie';

import { getFlagName } from '@hydra/api/middlewares/featureFlags';
import { convertKebabCaseToCamelCase } from '@hydra/utils/caseConvert';

interface FeatureFlags {
  [flag: string]: 'enabled'
}

interface CookieValues {
  [name: string]: string
}

export const FeatureFlagsContext = React.createContext<FeatureFlags>({});

export const parseFlags = (cookies: CookieValues) => {
  return Object.keys(cookies).reduce((flags, key) => {
    const flagName = getFlagName(key);
    if (flagName && cookies[key] === 'enabled') {
      const camelCaseKey = convertKebabCaseToCamelCase(flagName);
      flags[camelCaseKey] = 'enabled';
    }

    return flags;
  }, {} as FeatureFlags);
};

interface FeatureFlagsProviderProps {
  serverCookies?: CookieValues
  children?: React.ReactNode
}

export function FeatureFlagsProvider (props: FeatureFlagsProviderProps) {
  const cookies = (props && props.serverCookies)
    ? props.serverCookies
    : new Cookies().getAll();

  return (
    <FeatureFlagsContext.Provider value={parseFlags(cookies)}>
      {props.children}
    </FeatureFlagsContext.Provider>
  );
}

export default function withFeatureFlags<P = {}> (Component: React.ComponentType<P>) {
  return function FeatureFlagComponent (props: P) {
    return (
      <FeatureFlagsContext.Consumer>
        {context => {
          return (
            <Component
              {...props}
              featureFlags={context}
            />
          );
        }}
      </FeatureFlagsContext.Consumer>
    );
  };
}

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
